import type {AlternateLanguage, ClientConfig, LinkField} from '@prismicio/client'
import {isFilled, createClient as prismicCreateClient} from '@prismicio/client'
import * as prismicNext from '@prismicio/next/pages'
import {useSearchParams} from 'next/navigation'
import {useRouter} from 'next/router'
import pRetry from 'p-retry'
import prismicConfig from 'prismic.config.json'
import type {
  AllDocumentTypes,
  BlogPostDocumentData,
  GlossaryTermDocument,
  PageDocument,
  PageDocumentData,
} from 'prismicio-types'
import {useGetOriginPageParam} from 'src/common/hooks/useGetPricingFormHandler/useGetPricingFormHandler'
import {logger} from 'src/common/logger'
import {AI_HUB_ITEM_DOCUMENT_TYPES, AI_HUB_PAGE_UID} from 'src/common/utils/aiHubRoutes'
import {getLanguageCode, isLeadFunnelUrl} from 'src/common/utils/locale'
import {usePageContext} from 'src/context/PageContext'

const repositoryName = process.env.NEXT_PUBLIC_PRISMIC_REPOSITORY_NAME || prismicConfig.repositoryName

function sleep(time: number) {
  return new Promise((resolve) => setTimeout(resolve, time))
}

const PRISMIC_FETCH_RETRIES = 5

// The query string carries the ref and access token, so it must never reach the logs.
function stripQueryString(url: string): string {
  return url.split('?')[0]
}

const baseUrl = 'https://www.getmoss.com'
const defaultLocale = 'en-us'

export function resolvedComputedPath(pageData: Pick<PageDocumentData, 'computed_path'> | undefined): string | null {
  if (!pageData || !isFilled.keyText(pageData.computed_path)) {
    return null
  }

  const normalized = pageData.computed_path.trim().replace(/\/+$/, '')

  return normalized.length > 0 ? normalized : null
}

export function resolveParentBasePath(parentPage: {data?: unknown; uid?: string | null}): string | null {
  const computedPath = resolvedComputedPath(parentPage.data as Pick<PageDocumentData, 'computed_path'> | undefined)

  if (computedPath) {
    return computedPath
  }

  if (parentPage.uid) {
    return `/${parentPage.uid}`
  }

  return null
}

type ResolvableLink = LinkField | AlternateLanguage | AllDocumentTypes

function isGlossaryTerm(link: ResolvableLink): link is GlossaryTermDocument {
  return 'type' in link && link.type === 'glossary_term'
}

function getGlossaryTermParentUid(link: ResolvableLink): string | null {
  if (!isGlossaryTerm(link) || !('data' in link) || !isFilled.contentRelationship(link.data.parent_page)) {
    return null
  }

  return link.data.parent_page.uid ?? null
}

export const useLinkResolver = () => {
  const searchParams = useSearchParams()
  const query = Object.fromEntries(searchParams)
  delete query.hide_nav
  const originPage = useGetOriginPageParam()
  const {funnelType} = usePageContext()
  const {locale} = useRouter()
  const lang = getLanguageCode(locale)

  // Add funnel_type to query if present
  const queryWithFunnelType = {
    ...query,
    ...(funnelType ? {funnel_type: funnelType} : {}),
  }

  const linkResolver = (link?: ResolvableLink, element?: string) => {
    if (!link) {
      return {
        pathname: '/',
        query: queryWithFunnelType,
      }
    }

    if ('link_type' in link && !isFilled.link(link)) {
      return {
        pathname: '/',
        query: queryWithFunnelType,
      }
    }

    if ('link_type' in link && link.link_type === 'Media') {
      return {
        pathname: link.url || '/',
        query: queryWithFunnelType,
      }
    }

    if ('link_type' in link && link.link_type === 'Web') {
      if (link.url && link.url[0] === '#') {
        return {
          hash: link.url,
          query: queryWithFunnelType,
          pathname: '',
        }
      }

      const url = new URL(link.url)
      const urlWithoutQuery = link.url?.split('?')[0]
      const queryFromLink = Object.fromEntries(url.searchParams.entries())
      queryFromLink['origin-page'] = originPage

      return {
        pathname: urlWithoutQuery,
        query: {
          ...queryWithFunnelType,
          ...(isLeadFunnelUrl(url) ? {lang} : {}),
          ...queryFromLink,
          ...(element ? {element} : {}),
        },
      }
    }

    if (link.type === 'home_page') {
      return {
        pathname: link.lang === defaultLocale ? '/' : `/${link.lang}`,
        query: queryWithFunnelType,
      }
    }

    if (link.type === '404_page') {
      return {
        pathname: `${link.lang === defaultLocale ? '' : `/${link.lang}`}/404`,
        query: queryWithFunnelType,
      }
    }

    if (
      link.type === 'blog_post' &&
      'data' in link &&
      isFilled.contentRelationship((link.data as PageDocumentData).parent_page)
    ) {
      const parentPage = (link.data as PageDocumentData).parent_page as unknown as PageDocument
      const parentPath = resolvedComputedPath(parentPage.data) ?? `/${parentPage.uid}`
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}${parentPath}/${link.uid}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (
      link.type === 'article' &&
      'data' in link &&
      isFilled.contentRelationship((link.data as PageDocumentData).parent_page)
    ) {
      const parentPage = (link.data as PageDocumentData).parent_page as unknown as PageDocument
      const parentPath = resolvedComputedPath(parentPage.data) ?? `/${parentPage.uid}`
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}${parentPath}/${link.uid}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (
      link.type === 'customer_story' &&
      'data' in link &&
      isFilled.contentRelationship((link.data as PageDocumentData).parent_page)
    ) {
      const parentPage = (link.data as PageDocumentData).parent_page as unknown as PageDocument
      const parentPath = resolvedComputedPath(parentPage.data) ?? `/${parentPage.uid}`
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}${parentPath}/${link.uid}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (link.type === 'event') {
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}/events-hub/${link.uid}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (link.type === 'ai_hub_page') {
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${AI_HUB_PAGE_UID}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (AI_HUB_ITEM_DOCUMENT_TYPES.has(link.type) && 'uid' in link && link.uid) {
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${AI_HUB_PAGE_UID}/${link.uid}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (link.type === 'ai_hub_row' && 'uid' in link && link.uid) {
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${AI_HUB_PAGE_UID}/rows/${link.uid}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (
      link.type === 'page' &&
      'data' in link &&
      isFilled.contentRelationship((link.data as PageDocumentData).parent_page)
    ) {
      const parentPage = (link.data as PageDocumentData).parent_page as unknown as PageDocument
      const path = resolvedComputedPath(link.data as PageDocumentData) ?? `/${parentPage.uid}/${link.uid}`
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}${path}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (link.type === 'page') {
      const path = ('data' in link && resolvedComputedPath(link.data as PageDocumentData)) || `/${link.uid}`
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}${path}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (link.type === 'glossary_page') {
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${link.uid}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    const glossaryTermParentUid = getGlossaryTermParentUid(link)

    if (link.type === 'glossary_term' && glossaryTermParentUid) {
      const pathname = `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${glossaryTermParentUid}/${link.uid}`

      return {
        pathname,
        query: queryWithFunnelType,
      }
    }

    if (link.type === 'customer_story' && 'url' in link) {
      return {
        pathname: link.url || '/',
        query: queryWithFunnelType,
      }
    }

    return {
      pathname: '/',
      query: queryWithFunnelType,
    }
  }

  return {linkResolver}
}

export type LinkResolverFn = ReturnType<typeof useLinkResolver>['linkResolver']

export const linkResolver = (link: ResolvableLink) => {
  if ('link_type' in link && !isFilled.link(link)) {
    return '/'
  }

  if ('link_type' in link && link.link_type === 'Media') {
    return '/'
  }

  if ('link_type' in link && link.link_type === 'Web') {
    return link.url?.replace(baseUrl, '') || '/'
  }

  if (
    link.type === 'blog_post' &&
    'data' in link &&
    isFilled.contentRelationship((link.data as BlogPostDocumentData).parent_page)
  ) {
    const parentPage = (link.data as BlogPostDocumentData).parent_page as unknown as PageDocument
    const parentPath = resolvedComputedPath(parentPage.data) ?? `/${parentPage.uid}`

    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}${parentPath}/${link.uid}`
  }

  if (link.type === 'home_page') {
    return link.lang === defaultLocale ? '/' : `/${link.lang}`
  }

  if (link.type === '404_page') {
    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}/404`
  }

  if (link.type === 'ai_hub_page') {
    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${AI_HUB_PAGE_UID}`
  }

  if (AI_HUB_ITEM_DOCUMENT_TYPES.has(link.type) && 'uid' in link && link.uid) {
    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${AI_HUB_PAGE_UID}/${link.uid}`
  }

  if (link.type === 'ai_hub_row' && 'uid' in link && link.uid) {
    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${AI_HUB_PAGE_UID}/rows/${link.uid}`
  }

  if (
    link.type === 'article' &&
    'data' in link &&
    isFilled.contentRelationship((link.data as PageDocumentData).parent_page)
  ) {
    const parentPage = (link.data as PageDocumentData).parent_page as unknown as PageDocument
    const parentPath = resolvedComputedPath(parentPage.data) ?? `/${parentPage.uid}`

    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}${parentPath}/${link.uid}`
  }

  if (
    link.type === 'customer_story' &&
    'data' in link &&
    isFilled.contentRelationship((link.data as PageDocumentData).parent_page)
  ) {
    const parentPage = (link.data as PageDocumentData).parent_page as unknown as PageDocument
    const parentPath = resolvedComputedPath(parentPage.data) ?? `/${parentPage.uid}`

    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}${parentPath}/${link.uid}`
  }

  if (
    link.type === 'page' &&
    'data' in link &&
    isFilled.contentRelationship((link.data as PageDocumentData).parent_page)
  ) {
    const parentPage = (link.data as PageDocumentData).parent_page as unknown as PageDocument
    const path = resolvedComputedPath(link.data as PageDocumentData) ?? `/${parentPage.uid}/${link.uid}`

    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}${path}`
  }

  if (link.type === 'page') {
    const path = ('data' in link && resolvedComputedPath(link.data as PageDocumentData)) || `/${link.uid}`

    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}${path}`
  }

  if (link.type === 'glossary_page') {
    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${link.uid}`
  }

  const glossaryTermParentUid = getGlossaryTermParentUid(link)

  if (link.type === 'glossary_term' && glossaryTermParentUid) {
    return `${link.lang === defaultLocale ? '' : `/${link.lang}`}/${glossaryTermParentUid}/${link.uid}`
  }

  return '/'
}

const routes: ClientConfig['routes'] = [
  {
    type: 'home_page',
    path: '/:lang?',
  },

  {
    type: '404_page',
    path: '/:lang?/404',
  },

  {
    type: 'article',
    resolvers: {
      magazine: 'parent_page',
    },
    path: '/:lang?/:magazine/:uid',
  },

  {
    type: 'customer_story',
    resolvers: {
      stories: 'parent_page',
    },
    path: '/:lang?/:stories/:uid',
  },

  {
    type: 'page',
    path: '/:lang?/:uid',
  },

  {
    type: 'page',
    resolvers: {
      parent: 'parent_page',
    },
    path: '/:lang?/:parent?/:uid',
  },

  {
    type: 'glossary_page',
    path: '/:lang?/:uid',
  },

  {
    type: 'glossary_term',
    resolvers: {
      parent: 'parent_page',
    },
    path: '/:lang?/:parent/:uid',
  },
]

export const createClient = ({previewData, req, ...config}: prismicNext.CreateClientConfig = {}) => {
  const client = prismicCreateClient(repositoryName, {
    ...config,
    routes,

    fetch: async (url, options) => {
      const urlWithoutQuery = stripQueryString(url)

      try {
        const response = await pRetry(() => fetch(url, options), {
          retries: PRISMIC_FETCH_RETRIES,
          maxRetryTime: 15000,

          onFailedAttempt: async (error) => {
            logger.warn(
              'Retrying Prismic request',
              {url: urlWithoutQuery, attempt: error.attemptNumber, maxAttempts: PRISMIC_FETCH_RETRIES + 1},
              error,
            )

            await sleep(500)
          },
        })

        if (!response.ok) {
          logger.warn('Prismic responded with an error status', {url: urlWithoutQuery, status: response.status})
        }

        return response
      } catch (error) {
        logger.error('Prismic request failed too many times', {url: urlWithoutQuery}, error)
        throw new Error('Prismic client network request failed too many times', {cause: error})
      }
    },
  })

  prismicNext.enableAutoPreviews({client, previewData, req})
  return client
}
